import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useTasks } from "@/hooks/useTasks";
import { useLeads } from "@/hooks/useLeads";
import { useBankers } from "@/hooks/useBankers";
import { useChannelPartners } from "@/hooks/useChannelPartners";
import { getLegacyCustomerName, Task } from "@/types";
import { Bell, Clock, ChevronRight, Check, AlertTriangle, CalendarClock, Paperclip } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { format, isToday, isPast, isTomorrow, parseISO, formatDistanceToNow } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { isBasicUser } from "@/lib/rbac";
import { useNavigate } from "react-router-dom";
import { ViewEditTaskDialog } from "./ViewEditTaskDialog";
import { toast } from "sonner";
import { getAllActiveUsers } from "@/services/userService";


type UrgencyGroup = "overdue" | "today" | "upcoming";

const URGENCY_CONFIG: Record<UrgencyGroup, { label: string; icon: typeof AlertTriangle; color: string; badgeVariant: string; borderColor: string }> = {
  overdue: {
    label: "Overdue",
    icon: AlertTriangle,
    color: "text-red-600 dark:text-red-400",
    badgeVariant: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    borderColor: "border-l-red-500",
  },
  today: {
    label: "Due Today",
    icon: Clock,
    color: "text-orange-600 dark:text-orange-400",
    badgeVariant: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
    borderColor: "border-l-orange-500",
  },
  upcoming: {
    label: "Upcoming",
    icon: CalendarClock,
    color: "text-blue-600 dark:text-blue-400",
    badgeVariant: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
    borderColor: "border-l-blue-500",
  },
};

function getUrgencyGroup(task: Task): UrgencyGroup {
  if (!task.dueDate) return "upcoming";
  const dueDate = typeof task.dueDate === 'string' ? parseISO(task.dueDate) : new Date(task.dueDate);
  if (isToday(dueDate)) return "today";
  if (isPast(dueDate)) return "overdue";
  return "upcoming";
}

function getRelativeTime(task: Task): string {
  if (!task.dueDate) return "";
  const dueDate = typeof task.dueDate === 'string' ? parseISO(task.dueDate) : new Date(task.dueDate);
  
  if (isToday(dueDate)) {
    const timeStr = format(dueDate, "h:mm a");
    return `Today at ${timeStr}`;
  }
  if (isTomorrow(dueDate)) {
    const timeStr = format(dueDate, "h:mm a");
    return `Tomorrow at ${timeStr}`;
  }
  if (isPast(dueDate)) {
    return `${formatDistanceToNow(dueDate)} overdue`;
  }
  return `Due ${formatDistanceToNow(dueDate, { addSuffix: true })}`;
}

export function TaskList({ onAddReminder }: { onAddReminder?: () => void }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isBasic = isBasicUser(user);
  const { tasks, updateTask } = useTasks();
  const { leads } = useLeads();
  const { bankers } = useBankers();
  const { partners: channelPartners = [] } = useChannelPartners();
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState<string | null>(null);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);

  useEffect(() => {
    getAllActiveUsers().then(users => setAvailableUsers(users)).catch(() => {});
  }, []);

  const getCreatorFirstName = (task: Task): string => {
    // Use denormalized name if available (new tasks)
    if (task.createdByName) return task.createdByName.split(' ')[0];
    // Fall back to resolving user ID → name (legacy tasks)
    if (!task.createdBy) return '';
    const name = task.createdBy;
    if (name.includes('user_') || (name.length > 20 && !name.includes(' '))) {
      const found = availableUsers.find(u => (u.userId || u.id) === name);
      return found?.name?.split(' ')[0] || '';
    }
    return name.split(' ')[0];
  };

  // Get all incomplete tasks, sorted by urgency then date
  // Dashboard shows only overdue and today's reminders (not upcoming)
  const pendingTasks = tasks
    .filter(t => {
      if (!t.type || t.type === 'reminder') {
        if (t.completed) return false;
        const urgency = getUrgencyGroup(t);
        return urgency === 'overdue' || urgency === 'today';
      }
      return false;
    })
    .sort((a, b) => {
      const urgencyOrder: Record<UrgencyGroup, number> = { overdue: 0, today: 1, upcoming: 2 };
      const urgencyA = urgencyOrder[getUrgencyGroup(a)];
      const urgencyB = urgencyOrder[getUrgencyGroup(b)];
      if (urgencyA !== urgencyB) return urgencyA - urgencyB;
      
      const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      return dateA - dateB;
    });

  // Group tasks by urgency
  const grouped = pendingTasks.reduce<Record<UrgencyGroup, Task[]>>(
    (acc, task) => {
      const group = getUrgencyGroup(task);
      acc[group].push(task);
      return acc;
    },
    { overdue: [], today: [], upcoming: [] }
  );

  const handleCompleteRequest = (e: React.MouseEvent, taskId: string) => {
    e.stopPropagation();
    setTaskToComplete(taskId);
    setConfirmCompleteOpen(true);
  };

  const handleConfirmComplete = async () => {
    if (taskToComplete) {
      await updateTask(taskToComplete, { completed: true });
      toast.success("Reminder completed");
    }
    setConfirmCompleteOpen(false);
    setTaskToComplete(null);
  };

  const handleTaskUpdate = async (updatedTask: Task) => {
    await updateTask(updatedTask.id, updatedTask);
    setSelectedTask(null);
  };

  const getLeadForTask = (task: Task) => {
    return leads.find(l => l.id === task.leadId);
  };

  const totalCount = pendingTasks.length;
  const overdueCount = grouped.overdue.length;

  const renderTaskCard = (task: Task, index: number) => {
    const lead = getLeadForTask(task);
    const banker = bankers.find(b => b.id === task.smContactId);
    const partner = channelPartners.find(p => p.id === task.connectorId);
    
    // Determine which contact to show (preference: Customer > Banker > Partner)
    let contactName = '';
    let contactLabel = '';
    if (lead) {
      contactName = getLegacyCustomerName(lead);
      contactLabel = '';
    } else if (banker) {
      contactName = banker.name;
      contactLabel = 'B: ';
    } else if (partner) {
      contactName = partner.name;
      contactLabel = 'V: ';
    }
    
    const urgency = getUrgencyGroup(task);
    const config = URGENCY_CONFIG[urgency];
    const relativeTime = getRelativeTime(task);

    return (
      <motion.div
        key={task.id}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.03 }}
        className={cn(
          "group relative rounded-lg border border-l-4 bg-card p-3 cursor-pointer",
          "hover:shadow-md transition-all duration-200",
          config.borderColor
        )}
        onClick={() => setSelectedTask(task)}
      >
        <div className="flex items-start gap-3">
          {/* Complete checkbox + Attachment indicator */}
          <div className="flex flex-col items-center gap-1">
            <button
              onClick={(e) => handleCompleteRequest(e, task.id)}
              className={cn(
                "mt-0.5 flex-shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center",
                "hover:bg-success/20 hover:border-success transition-colors",
                urgency === 'overdue' ? 'border-red-400' : urgency === 'today' ? 'border-orange-400' : 'border-muted-foreground/40'
              )}
            >
              <Check className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
            </button>
            {/* Attachment count */}
            {task.attachments && task.attachments.length > 0 && (
              <div className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                <Paperclip className="h-3 w-3" />
                <span className="font-medium">{task.attachments.length}</span>
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0 space-y-1">
            {/* Description - show full text */}
            <p className="text-sm font-medium leading-snug text-foreground">
              {task.description || task.title}
            </p>
            
            {/* Contact & Meta Row - Show one contact with preference: Customer > Banker > Partner */}
            <div className="flex flex-wrap items-center gap-1.5">
              {contactName && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 font-normal">
                  {contactLabel && <span className="font-semibold">{contactLabel}</span>}
                  {contactName}
                </Badge>
              )}
              <span className={cn("text-[11px] font-medium", config.color)}>
                {relativeTime}{getCreatorFirstName(task) ? ` · ${getCreatorFirstName(task)}` : ''}
              </span>
            </div>
          </div>
          
          {/* Arrow */}
          <ChevronRight className="h-4 w-4 text-muted-foreground/40 flex-shrink-0 mt-0.5 group-hover:text-muted-foreground transition-colors" />
        </div>
      </motion.div>
    );
  };

  const renderGroup = (group: UrgencyGroup) => {
    const groupTasks = grouped[group];
    if (groupTasks.length === 0) return null;
    const config = URGENCY_CONFIG[group];
    const Icon = config.icon;

    return (
      <div key={group} className="space-y-1.5">
        <div className="flex items-center gap-1.5 px-1">
          <Icon className={cn("h-3.5 w-3.5", config.color)} />
          <span className={cn("text-xs font-semibold uppercase tracking-wider", config.color)}>
            {config.label}
          </span>
          <Badge className={cn("text-[10px] px-1.5 py-0 h-4 ml-1", config.badgeVariant)}>
            {groupTasks.length}
          </Badge>
        </div>
        <div className="space-y-1.5">
          {groupTasks.map((task, i) => renderTaskCard(task, i))}
        </div>
      </div>
    );
  };

  return (
    <>
      <Card variant="elevated" className="overflow-hidden w-full min-w-0">
        {/* Label on top + Add Reminder button for Basic User */}
        {isBasic && (
          <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 px-4 pt-3 pb-0">
            <CardTitle className="flex items-center gap-1.5 text-sm font-semibold">
              <Bell className="h-4 w-4 text-green-600" />
              Today's Reminders
              {totalCount > 0 && (
                <Badge className="text-[10px] px-1.5 py-0 h-4 bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                  {totalCount}
                </Badge>
              )}
            </CardTitle>
            {onAddReminder && (
              <Button
                onClick={onAddReminder}
                size="sm"
                variant="outline"
                className="h-8 text-xs border-green-500 text-green-600 hover:bg-green-50"
              >
                <Bell className="h-3.5 w-3.5 mr-1.5" />
                Add Reminder
              </Button>
            )}
          </CardHeader>
        )}
        <CardContent className="space-y-3 pt-4 pb-4">
          {totalCount === 0 ? (
            // Empty state
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center py-8 text-center"
            >
              <div className="rounded-full bg-success/10 p-4 mb-3">
                <Check className="h-8 w-8 text-success" />
              </div>
              <p className="text-sm font-medium text-foreground">You're all caught up! 🎉</p>
              <p className="text-xs text-muted-foreground mt-1">No pending reminders</p>
            </motion.div>
          ) : (
            <>
              {/* Show only Overdue and Today Groups */}
              <AnimatePresence mode="popLayout">
                {(["overdue", "today"] as UrgencyGroup[]).map(renderGroup)}
              </AnimatePresence>
            </>
          )}
          
          {/* View all link */}
          <Button 
            variant="ghost" 
            className="w-full mt-2 text-accent text-sm h-10"
            onClick={() => navigate('/tasks')}
          >
            View all reminders
          </Button>
        </CardContent>
      </Card>

      {selectedTask && (
        <ViewEditTaskDialog 
          open={!!selectedTask} 
          onOpenChange={(open) => !open && setSelectedTask(null)}
          task={selectedTask}
          onSave={handleTaskUpdate}
        />
      )}

      {/* Confirm Complete Dialog */}
      <AlertDialog open={confirmCompleteOpen} onOpenChange={setConfirmCompleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as Completed?</AlertDialogTitle>
            <AlertDialogDescription>
              This reminder will be moved to the completed section.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmComplete} className="bg-success hover:bg-success/90">
              Complete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}